(() => {
  const STORAGE_KEY = "joiuss_saved";

  /**
   * Saved item shape:
   * {
   *   handle: string,
   *   title?: string,
   *   url?: string,
   *   image?: string,
   *   saved_at?: number
   * }
   */

  const isObject = (v) => v && typeof v === "object" && !Array.isArray(v);
  const normalizeHandle = (h) => String(h || "").trim();

  // Convert legacy ["handle1","handle2"] into [{handle:"handle1"},...]
  const normalizeSaved = (raw) => {
    if (!raw) return [];

    // Legacy: array of strings
    if (Array.isArray(raw) && raw.every((x) => typeof x === "string")) {
      return raw
        .map((h) => normalizeHandle(h))
        .filter(Boolean)
        .map((h) => ({ handle: h, saved_at: Date.now() }));
    }

    // New: array of objects
    if (
      Array.isArray(raw) &&
      raw.every((x) => isObject(x) && typeof x.handle === "string")
    ) {
      return raw
        .map((item) => ({
          ...item,
          handle: normalizeHandle(item.handle),
          saved_at: item.saved_at || Date.now(),
        }))
        .filter((item) => item.handle);
    }

    return [];
  };

  const readSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return normalizeSaved(parsed);
    } catch (e) {
      return [];
    }
  };

  const writeSaved = (arr) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    window.dispatchEvent(
      new CustomEvent("joiuss:saved-updated", { detail: { saved: arr } })
    );
  };

  const findIndexByHandle = (arr, handle) =>
    arr.findIndex((x) => x.handle === handle);

  const isSaved = (handle, arr) => findIndexByHandle(arr, handle) !== -1;

  const buildSavedItemFromDOM = (handle, wrapEl) => {
    // Wrap is .joiuss-save-wrap from your snippet.
    // We try to capture: title, url, image.
    const item = { handle, saved_at: Date.now() };

    // URL: best effort
    const nearestProductLink =
      wrapEl?.closest("[data-product-handle]")?.querySelector('a[href*="/products/"]') ||
      document.querySelector('link[rel="canonical"]');

    if (nearestProductLink) {
      if (nearestProductLink.tagName === "A") {
        item.url = nearestProductLink.getAttribute("href") || "";
      }
      if (nearestProductLink.tagName === "LINK") {
        item.url = nearestProductLink.getAttribute("href") || "";
      }
    }

    // Title (product page: h1)
    const h1 = document.querySelector("h1");
    if (h1 && h1.textContent) item.title = h1.textContent.trim();

    // Image (best effort: first product media img on page)
    const img =
      document.querySelector('[data-product-media] img') ||
      document.querySelector(".product__media img") ||
      document.querySelector('img[src*="cdn.shopify.com"]');

    if (img && img.getAttribute("src")) item.image = img.getAttribute("src");

    return item;
  };

  const addItem = (item) => {
    const saved = readSaved();
    const idx = findIndexByHandle(saved, item.handle);

    if (idx !== -1) {
      // Merge new info onto existing
      saved[idx] = { ...saved[idx], ...item };
      writeSaved(saved);
      return saved;
    }

    writeSaved([item, ...saved]);
    return [item, ...saved];
  };

  const removeHandle = (handle) => {
    const saved = readSaved().filter((x) => x.handle !== handle);
    writeSaved(saved);
    return saved;
  };

  const toggleSaved = (handle, wrapEl) => {
    const saved = readSaved();
    if (isSaved(handle, saved)) return removeHandle(handle);

    const item = buildSavedItemFromDOM(handle, wrapEl);
    return addItem(item);
  };

  // Drawer controls
  const drawer = () => document.querySelector(".joiuss-saved-drawer");

  const openDrawer = () => {
    const el = drawer();
    if (!el) return;
    el.hidden = false;
    el.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("joiuss-saved-open");
  };

  const closeDrawer = () => {
    const el = drawer();
    if (!el) return;
    el.hidden = true;
    el.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("joiuss-saved-open");
  };

  // Fetch Shopify product JSON (for enrichment + images if missing)
  const fetchProduct = async (handle) => {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error("product fetch failed");
    return await res.json();
  };

  const safeText = (v) => (v == null ? "" : String(v));

  // Render drawer items
  const renderDrawer = async () => {
    const el = drawer();
    if (!el) return;

    const list = el.querySelector("[data-joiuss-saved-list]");
    const empty = el.querySelector("[data-joiuss-saved-empty]");
    if (!list || !empty) return;

    const saved = readSaved();
    list.innerHTML = "";
    empty.hidden = saved.length !== 0;
    if (!saved.length) return;

    const products = await Promise.allSettled(saved.map((s) => fetchProduct(s.handle)));

    products.forEach((p, idx) => {
      const item = saved[idx];
      if (!item) return;

      const prod = p.status === "fulfilled" ? p.value : null;

      const title = item.title || (prod && prod.title) || item.handle;
      const img = item.image || (prod && prod.featured_image) || "";
      const meta = prod && prod.vendor ? prod.vendor : "";

      const row = document.createElement("div");
      row.className = "joiuss-saved-item";
      row.innerHTML = `
        <img class="joiuss-saved-item__img" src="${safeText(img)}" alt="${safeText(title)}">
        <div>
          <p class="joiuss-saved-item__title">${safeText(title)}</p>
          <p class="joiuss-saved-item__meta">${safeText(meta)}</p>
        </div>
        <button class="joiuss-saved-item__remove" type="button" aria-label="Remove" data-remove="${safeText(item.handle)}">✕</button>
      `;
      list.appendChild(row);
    });

    list.querySelectorAll("[data-remove]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const h = btn.getAttribute("data-remove");
        removeHandle(h);
      });
    });
  };

  // Bind close buttons
  document.addEventListener("click", (e) => {
    const t = e.target;
    if (t && t.closest && t.closest("[data-joiuss-saved-close]")) closeDrawer();
  });

  // Bind heart buttons (your snippet outputs these)
  const bindHearts = () => {
    document.querySelectorAll(".joiuss-save-wrap").forEach((wrap) => {
      const handle = normalizeHandle(wrap.getAttribute("data-handle"));
      const btn = wrap.querySelector(".joiuss-save-btn");
      if (!handle || !btn) return;

      const sync = () => {
        const saved = readSaved();
        const on = isSaved(handle, saved);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
        wrap.classList.toggle("is-saved", on);
      };

      btn.addEventListener("click", async () => {
        toggleSaved(handle, wrap);
        await renderDrawer();
        openDrawer();
        sync();
      });

      window.addEventListener("joiuss:saved-updated", sync);
      sync();
    });
  };

  /**
   * Saved page helper: standard Try-On button markup for a saved item.
   * The click behavior is handled in joiuss-saved-studio.js (opens the Try-On modal).
   */
  const buildTryonButtonHTML = (item) => {
    if (!item) return "";
    const handle = safeText(item.handle);
    const url = safeText(item.url || (handle ? `/products/${handle}` : ""));
    return `
      <button
        type="button"
        class="joiuss-btn joiuss-btn--primary joiuss-tryon-btn"
        data-joiuss-tryon-btn
        data-handle="${handle}"
        data-url="${url}"
        aria-label="Try on"
      >Try on</button>
    `.trim();
  };

  // Initial
  document.addEventListener("DOMContentLoaded", async () => {
    // Migrate legacy data once (so older saves become objects)
    const saved = readSaved();
    writeSaved(saved);

    bindHearts();
    await renderDrawer();
  });

  window.addEventListener("joiuss:saved-updated", renderDrawer);

  // Expose helpers to studio page
  window.JOIUSS_SAVED = {
    readSaved,
    fetchProduct,
    buildTryonButtonHTML,
    remove: (handle) => {
      const h = String(handle || "").trim();
      const saved = readSaved().filter((x) => x.handle !== h);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
      window.dispatchEvent(
        new CustomEvent("joiuss:saved-updated", { detail: { saved } })
      );
      return saved;
    },
  };
})();
